<template>
  <div class="app-container">
    <div class="page-header">
      <div class="back-link" @click="goBack">
        <i class="el-icon-arrow-left"></i> 返回
      </div>
      <div class="page-title">作业详情</div>
      <!-- 添加保存按钮 -->
      <el-button type="primary" size="small" @click="saveHomework" class="save-btn">
        保存作业
      </el-button>
    </div>

    <!-- 作业详情内容 -->
    <div class="homework-detail-content">
      <!-- 作业基本信息 -->
      <div class="detail-section">
        <div class="section-title">基本信息</div>
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="info-item">
              <span class="label">作业名称：</span>
              <span class="value">{{ currentHomework.title || '暂无数据' }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <span class="label">发布时间：</span>
              <span class="value">{{ currentHomework.publishTime || '暂无数据' }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <span class="label">截止时间：</span>
              <span class="value">{{ currentHomework.deadline || '暂无数据' }}</span>
            </div>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="info-item">
              <span class="label">班级：</span>
              <span class="value">{{ currentHomework.className || '暂无数据' }}</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <span class="label">完成率：</span>
              <span class="value">75%</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="info-item">
              <span class="label">平均分：</span>
              <span class="value">{{ averageScore }} 分</span>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 分数分布部分 -->
      <div class="detail-section">
        <div class="section-title">分数分布</div>
        <div class="score-distribution">
          <div class="chart-container" v-loading="loading">
            <div v-if="!loading && Object.keys(statsData.scoreDistribution).length > 0" class="chart">
              <el-row :gutter="20">
                <el-col :span="12">
                  <div class="distribution-item" v-for="(count, range) in statsData.scoreDistribution" :key="range">
                    <span class="range-label">{{ range }}:</span>
                    <el-progress
                      :percentage="calculatePercentage(count)"
                      :color="getColorForRange(range)"
                      :format="() => count + '人'"
                    ></el-progress>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="stats-summary">
                    <div class="summary-item">
                      <div class="summary-label">完成率</div>
                      <div class="summary-value">{{ statsData.completionRate }}%</div>
                    </div>
                    <div class="summary-item">
                      <div class="summary-label">平均分</div>
                      <div class="summary-value">{{ averageScore }}</div>
                    </div>
                  </div>
                </el-col>
              </el-row>
            </div>
            <div v-else-if="!loading" class="no-data">暂无分数分布数据</div>
          </div>
        </div>
      </div>

      <!-- 学生提交情况表格 -->
      <div class="detail-section">
        <div class="section-title">提交情况</div>
        <el-table
          :data="submissions"
          border
          fit
          highlight-current-row
          :header-cell-style="{
            background: '#f2f3f4',
            color: '#555',
            'font-weight': 'bold',
            'line-height': '32px',
          }"
        >
          <el-table-column label="序号" align="center" width="80">
            <template slot-scope="scope">{{ scope.$index + 1 }}</template>
          </el-table-column>
          <el-table-column prop="studentName" label="学生姓名" align="center" />
          <el-table-column prop="studentId" label="学号" align="center" />
          <el-table-column prop="submitTime" label="提交时间" align="center" />
          <el-table-column prop="status" label="状态" align="center">
            <template slot-scope="{row}">
              <el-tag :type="row.status === '已批改' ? 'success' : 'warning'">
                {{ row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="score" label="得分" align="center" />
          <el-table-column label="操作" align="center" width="150">
            <template slot-scope="{row}">
              <el-button type="text" size="small" @click="viewSubmission(row)">查看</el-button>
              <el-button
                v-if="row.status !== '已批改'"
                type="text"
                size="small"
                @click="markHomework(row)"
              >批改</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            :current-page="currentPage"
            :page-sizes="[10, 20, 30, 50]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
// 添加缺少的API函数
const getHomeworkDetail = async (homeworkId) => {
  try {
    // 修改API端点，使用与后端匹配的URL
    const response = await axios.get(`/api/homework/list`)
    if (response.data && response.data.code === 1 && response.data.data) {
      // 从列表中筛选出指定ID的作业
      const homework = response.data.data.find(item => item.id === homeworkId)
      if (homework) {
        return {
          code: 1,
          data: {
            id: homework.id,
            title: homework.name || homework.title,
            publishTime: homework.createTime || homework.publishTime,
            deadline: homework.deadline,
            className: homework.className || `${homework.subject}班`,
            description: homework.description
          },
          message: '获取作业详情成功'
        }
      }
    }
    return { code: 0, message: '未找到指定作业' }
  } catch (error) {
    console.error('API调用失败:', error)
    return { code: 0, message: '获取作业详情失败' }
  }
}

// 添加统计信息API函数
const getHomeworkStats = async (homeworkId) => {
  try {
    // 由于后端暂未提供专门的统计信息接口，这里使用模拟数据
    // 实际项目中，应该调用后端提供的API
    // 模拟数据
    const mockStats = {
      completionRate: 75,
      averageScore: 85,
      scoreDistribution: {
        '0-60分': 5,
        '60-80分': 15,
        '80-100分': 30
      }
    }

    return {
      code: 1,
      data: mockStats,
      message: '获取统计数据成功'
    }
  } catch (error) {
    console.error('API调用失败:', error)
    return { code: 0, message: '获取统计数据失败' }
  }
}

const getHomeworkSubmissions = async (homeworkId) => {
  try {
    // 调用后端API获取提交情况
    const response = await axios.get(`/api/student-submissions/list/${homeworkId}`)
    if (response.data) {
      return {
        code: 1,
        data: response.data,
        message: '获取提交情况成功'
      }
    }
    return { code: 0, message: '获取提交情况失败' }
  } catch (error) {
    console.error('API调用失败:', error)
    return { code: 0, message: '获取提交情况失败' }
  }
}

export default {
  name: 'HomeworkDetail',
  data() {
    return {
      currentHomework: {},
      submissions: [],
      completionRate: 0,
      averageScore: 0,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      statsData: {
        completionRate: 0,
        averageScore: 0,
        scoreDistribution: {}
      }
    }
  },
  computed: {
    // 移除不存在的roles getter
    roles() {
      // 从localStorage获取角色信息
      return localStorage.getItem('roles')
    }
  },
  created() {
    // 检查用户角色
    const userRole = this.roles
    if (userRole !== 'teacher' && userRole !== 'mteacher') {
      this.$message.error('您没有权限访问此页面')
      this.$router.push('/404')
    }
  },
  mounted() {
    this.fetchHomeworkDetail()
    this.fetchSubmissions()
    this.fetchHomeworkStats() // 添加获取统计信息的调用
    this.fetchAverageScore() // 添加获取平均分的调用
  },
  methods: {
    // 返回上一页
    goBack() {
      this.$router.push('/score-analysis/homework-analysis')
    },

    // 获取作业ID
    getHomeworkId() {
      // 优先从路由参数中获取ID
      const routeId = this.$route.params.id
      // 如果路由参数中没有ID，则从查询参数中获取
      const queryId = this.$route.query.id

      return routeId || queryId
    },

    // 获取作业详情
    async fetchHomeworkDetail() {
      const homeworkId = this.getHomeworkId()

      if (!homeworkId) {
        this.$message.warning('未找到作业ID，无法获取作业详情')
        // 使用模拟数据
        this.currentHomework = {
          id: 0,
          title: '示例作业',
          publishTime: '2023-10-01 09:00:00',
          deadline: '2023-10-15 23:59:59',
          className: '示例班级',
          description: '这是一个示例作业'
        }
        console.warn('使用模拟数据：未找到作业ID')
        return
      }

      try {
        const response = await getHomeworkDetail(homeworkId)
        if (response.code === 1 && response.data) {
          this.currentHomework = response.data
        } else {
          this.$message.error(response.message || '获取作业详情失败')
        }
      } catch (error) {
        console.error('获取作业详情失败:', error)
        this.$message.error('获取作业详情失败，请稍后重试')
      }
    },

    // 获取作业统计数据
    async fetchHomeworkStats() {
      const homeworkId = this.getHomeworkId()

      if (!homeworkId) {
        this.$message.warning('未找到作业ID，无法获取统计数据')
        // 使用模拟数据
        this.statsData = {
          completionRate: 75,
          averageScore: 85,
          scoreDistribution: {
            '0-60分': 5,
            '60-80分': 15,
            '80-100分': 30
          }
        }
        console.warn('使用模拟数据：未找到作业ID')
        return
      }

      try {
        const response = await getHomeworkStats(homeworkId)
        if (response.code === 1 && response.data) {
          this.statsData = response.data
        } else {
          this.$message.error(response.message || '获取统计数据失败')
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
        this.$message.error('获取统计数据失败，请稍后重试')
      }
    },

    // 获取提交情况
    async fetchSubmissions() {
      this.loading = true
      const homeworkId = this.getHomeworkId()

      if (!homeworkId) {
        this.$message.warning('未找到作业ID，无法获取提交情况')
        // 使用模拟数据
        this.submissions = [
          {
            id: 1,
            studentName: '张三',
            submitTime: '2023-10-05 14:30:00',
            status: '已批改',
            score: 85
          },
          {
            id: 2,
            studentName: '李四',
            submitTime: '2023-10-06 16:45:00',
            status: '已批改',
            score: 92
          },
          {
            id: 3,
            studentName: '王五',
            submitTime: '2023-10-07 09:15:00',
            status: '未批改',
            score: null
          }
        ]
        this.total = this.submissions.length
        this.loading = false
        console.warn('使用模拟数据：未找到作业ID')
        return
      }

      try {
        const response = await getHomeworkSubmissions(homeworkId)
        if (response.code === 1 && response.data) {
          this.submissions = response.data
          this.total = this.submissions.length
        } else {
          this.$message.error(response.message || '获取提交情况失败')
        }
      } catch (error) {
        console.error('获取提交情况失败:', error)
        this.$message.error('获取提交情况失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    // 计算百分比
    calculatePercentage(count) {
      // 确保 count 是数字
      count = Number(count) || 0
      const total = this.total || 1 // 避免除以0
      // 确保返回值在0-100之间
      const percentage = Math.round((count / total) * 100)
      return Math.min(Math.max(percentage, 0), 100) // 限制在0-100之间
    },

    // 根据分数段获取颜色
    getColorForRange(range) {
      const colorMap = {
        '60分以下': '#F56C6C',
        '60~79分': '#E6A23C',
        '80~100分': '#67C23A',
        '未获得分数': '#909399'
      }
      return colorMap[range] || '#409EFF'
    },

    // 添加分页处理函数
    handleSizeChange(val) {
      this.pageSize = val
      this.fetchSubmissions()
    },

    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchSubmissions()
    },

    // 查看提交详情
    viewSubmission(row) {
      // 实现查看提交详情的逻辑
      this.$message.info(`查看学生 ${row.studentName} 的提交`)
    },

    // 批改作业
    markHomework(row) {
      // 实现批改作业的逻辑
      this.$message.info(`批改学生 ${row.studentName} 的作业`)
    },

    // 保存作业数据到后端
    async saveHomework() {
      try {
        // 构建作业数据对象
        const homeworkData = {
          id: this.currentHomework.id,
          name: this.currentHomework.title,
          createTime: this.currentHomework.publishTime,
          deadline: this.currentHomework.deadline,
          className: this.currentHomework.className,
          description: this.currentHomework.description
          // 根据需要添加其他字段
        }

        // 调用后端API保存作业数据
        const response = await axios.post('/api/homework/save', homeworkData)

        if (response.data && response.data.code === 1) {
          this.$message.success(response.data.message || '作业数据保存成功')
          // 刷新作业详情
          this.fetchHomeworkDetail()
        } else {
          this.$message.error(response.data.message || '作业数据保存失败')
        }
      } catch (error) {
        console.error('保存作业数据失败:', error)
        this.$message.error('保存作业数据失败，请稍后重试')
      }
    },

    // 获取作业平均分
    async fetchAverageScore() {
      const homeworkId = this.getHomeworkId()
      console.log("课程Id：", homeworkId)

      if (!homeworkId) {
        this.$message.warning('未找到作业ID，无法获取平均分')
        // 使用默认值
        this.averageScore = 0
        console.warn('使用默认值：未找到作业ID')
        return
      }

      try {
        // 调用后端API获取平均分，修改为正确的端口号
        const response = await axios.get(`http://localhost:8080/api/student-submissions/average-point/${homeworkId}`)
        if (response.data && response.data.averagePoint !== undefined) {
          // 更新平均分
          this.averageScore = response.data.averagePoint || 0
        } else {
          this.$message.error('获取平均分失败')
          this.averageScore = 0
        }
      } catch (error) {
        console.error('获取平均分失败:', error)
        this.$message.error('获取平均分失败，请稍后重试')
        this.averageScore = 0
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.back-link {
  cursor: pointer;
  color: #409EFF;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.page-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 18px;
  font-weight: bold;
}
.save-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.detail-section {
  background-color: #fff;
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.info-item {
  margin-bottom: 15px;
}

.label {
  color: #606266;
  margin-right: 10px;
}

.value {
  font-weight: bold;
  color: #303133;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.score-distribution {
  margin-top: 20px;
}

.chart-container {
  min-height: 300px;
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.distribution-item {
  margin-bottom: 15px;
}

.range-label {
  display: inline-block;
  width: 100px;
  font-weight: bold;
  margin-right: 10px;
}

.stats-summary {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.summary-item {
  text-align: center;
  margin-bottom: 30px;
}

.summary-label {
  font-size: 16px;
  color: #606266;
  margin-bottom: 10px;
}

.summary-value {
  font-size: 28px;
  font-weight: bold;
  color: #409EFF;
}

.no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #909399;
  font-size: 14px;
}
</style>
